import {
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    Modal,
    Paper,
    Slide,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Container } from '../../../components'
import {
    BaseWorkorder,
    createWorkorderFromResponse,
    ModelMap,
    ServiceContractMap,
    SUPER_TRANSITION_REVERSAL,
    WorkorderStatus,
} from '../../../models'
import { WorkorderRow } from './WorkorderRow'
import CloseIcon from '@material-ui/icons/Close'
import { axiosInstance, isAccountManager } from '../../../helpers'
import { useAppDispatch, useUser } from '../../../hooks'
import { WorkorderResponse, updateBulkWorkorders } from '../../../store'
import { toast } from 'react-toastify'
import { hasPermission } from '../../../models/Users/services'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    workorders: (BaseWorkorder | WorkorderResponse)[]
    isOpen: boolean
    upStreamContractMap?: ServiceContractMap
    isVendor?: boolean
    handleBulkTransitionClose: () => void
    setSelectedWorkorders: (
        selectedWorkorders: ModelMap<BaseWorkorder | WorkorderResponse>,
    ) => void
    handleBulkEditClose: () => void
    handleDataFormClose?: () => void
    superTransitionWorkorders?: (
        workorders: TransitionStatusDict[],
        allJobsites?: boolean | undefined,
    ) => Promise<void>
}

export interface TransitionsDict {
    [status: string]: string[]
}

interface TransitionStatusDict {
    id: number
    status: string
    revert: boolean
}

type TransitionCountsDict = {
    [key in WorkorderStatus]: {
        validCount: number
        foundCount: number
    }
}

const transitions: TransitionsDict = {
    ASSIGNED: [
        WorkorderStatus.IN_PROGRESS,
        WorkorderStatus.COMPLETE,
        WorkorderStatus.GO_BACK,
        WorkorderStatus.PRE_APPROVED,
        WorkorderStatus.APPROVED,
        WorkorderStatus.INVOICED_OUT,
    ],
    IN_PROGRESS: [
        WorkorderStatus.ASSIGNED,
        WorkorderStatus.COMPLETE,
        WorkorderStatus.GO_BACK,
        WorkorderStatus.PRE_APPROVED,
        WorkorderStatus.APPROVED,
        WorkorderStatus.PAUSE,
        WorkorderStatus.INVOICED_OUT,
    ],
    PAUSE: [
        WorkorderStatus.IN_PROGRESS,
        WorkorderStatus.COMPLETE,
        WorkorderStatus.GO_BACK,
        WorkorderStatus.PRE_APPROVED,
        WorkorderStatus.APPROVED,
        WorkorderStatus.INVOICED_OUT,
    ],
    COMPLETE: [
        WorkorderStatus.PRE_APPROVED,
        WorkorderStatus.APPROVED,
        WorkorderStatus.INVOICED_OUT,
    ],
    GO_BACK: [
        WorkorderStatus.COMPLETE,
        WorkorderStatus.PRE_APPROVED,
        WorkorderStatus.APPROVED,
        WorkorderStatus.INVOICED_OUT,
    ],
    PRE_APPROVED: [WorkorderStatus.APPROVED, WorkorderStatus.INVOICED_OUT],
    APPROVED: [WorkorderStatus.INVOICED_OUT],
    INVOICED: [],
    INVOICED_OUT: [],
    INVOICE_REQUEST: [],
}

export const BulkTransitionEditor = (props: Props) => {
    const {
        workorders,
        isOpen,
        handleBulkTransitionClose: handleOpen,
        upStreamContractMap,
        setSelectedWorkorders,
        handleBulkEditClose,
        handleDataFormClose,
        isVendor,
        superTransitionWorkorders,
    } = props

    interface WorkorderTransitionToDict {
        workorder: BaseWorkorder
        toStatus: string | undefined
    }

    const dispatch = useAppDispatch()
    const { workspaceUser } = useUser()
    const userPermissionRoleIds =
        workspaceUser?.permission_roles.map((role) => role.id) ?? []

    const isApprovalRoleRestricted = (workorder: BaseWorkorder) => {
        const approvalRoleIds = workorder.approval_role_ids ?? []

        return (
            workorder.status === WorkorderStatus.COMPLETE &&
            approvalRoleIds.length > 0 &&
            !approvalRoleIds.some((roleId) =>
                userPermissionRoleIds.includes(roleId),
            )
        )
    }

    const canTransitionWorkorder = (
        workorder: BaseWorkorder,
        status: WorkorderStatus,
    ) => {
        if (
            status === WorkorderStatus.APPROVED &&
            isApprovalRoleRestricted(workorder)
        ) {
            return false
        }

        return transitions[workorder.status].includes(status)
    }

    const [workorderTransitionList, setWorkorderTransitionList] = useState<
        WorkorderTransitionToDict[]
    >([])
    const [handlingTransitions, setHandlingTransitions] = useState(false)

    useEffect(() => {
        setWorkorderTransitionList(
            workorders.map((wo) => {
                return { workorder: wo, toStatus: undefined }
            }),
        )
    }, [workorders])

    if (hasPermission(workspaceUser, SUPER_TRANSITION_REVERSAL)) {
        transitions[WorkorderStatus.PRE_APPROVED].push(WorkorderStatus.COMPLETE)
        transitions[WorkorderStatus.PRE_APPROVED].push(WorkorderStatus.GO_BACK)
        transitions[WorkorderStatus.PRE_APPROVED].push(
            WorkorderStatus.IN_PROGRESS,
        )
        transitions[WorkorderStatus.PRE_APPROVED].push(WorkorderStatus.ASSIGNED)
        transitions[WorkorderStatus.APPROVED].push(WorkorderStatus.COMPLETE)
        transitions[WorkorderStatus.APPROVED].push(WorkorderStatus.PRE_APPROVED)
        transitions[WorkorderStatus.APPROVED].push(WorkorderStatus.IN_PROGRESS)
        transitions[WorkorderStatus.APPROVED].push(WorkorderStatus.GO_BACK)
        transitions[WorkorderStatus.APPROVED].push(WorkorderStatus.ASSIGNED)
        transitions[WorkorderStatus.COMPLETE].push(WorkorderStatus.IN_PROGRESS)
        transitions[WorkorderStatus.COMPLETE].push(WorkorderStatus.GO_BACK)
        transitions[WorkorderStatus.COMPLETE].push(WorkorderStatus.ASSIGNED)
        transitions[WorkorderStatus.INVOICED_OUT].push(WorkorderStatus.COMPLETE)
        transitions[WorkorderStatus.INVOICED_OUT].push(
            WorkorderStatus.PRE_APPROVED,
        )
        transitions[WorkorderStatus.INVOICED_OUT].push(
            WorkorderStatus.IN_PROGRESS,
        )
        transitions[WorkorderStatus.INVOICED_OUT].push(WorkorderStatus.GO_BACK)
        transitions[WorkorderStatus.INVOICED_OUT].push(WorkorderStatus.ASSIGNED)
        transitions[WorkorderStatus.INVOICED_OUT].push(WorkorderStatus.APPROVED)
    }

    const totalCounts = workorderTransitionList.reduce<TransitionCountsDict>(
        (prev, currentWorkorder) => {
            const isInProgressValid = canTransitionWorkorder(
                currentWorkorder.workorder,
                WorkorderStatus.IN_PROGRESS,
            )
            const isCompleteValid = canTransitionWorkorder(
                currentWorkorder.workorder,
                WorkorderStatus.COMPLETE,
            )
            const isGoBackValid = canTransitionWorkorder(
                currentWorkorder.workorder,
                WorkorderStatus.GO_BACK,
            )
            const isPreApprovedValid = canTransitionWorkorder(
                currentWorkorder.workorder,
                WorkorderStatus.PRE_APPROVED,
            )
            const isApprovedValid = canTransitionWorkorder(
                currentWorkorder.workorder,
                WorkorderStatus.APPROVED,
            )
            const isPauseValid = canTransitionWorkorder(
                currentWorkorder.workorder,
                WorkorderStatus.PAUSE,
            )
            const isAssignedValid = canTransitionWorkorder(
                currentWorkorder.workorder,
                WorkorderStatus.ASSIGNED,
            )
            const isInvoicedOutValid = canTransitionWorkorder(
                currentWorkorder.workorder,
                WorkorderStatus.INVOICED_OUT,
            )

            const isToInProgress =
                currentWorkorder.toStatus === WorkorderStatus.IN_PROGRESS
            const isToComplete =
                currentWorkorder.toStatus === WorkorderStatus.COMPLETE
            const isToGoBack =
                currentWorkorder.toStatus === WorkorderStatus.GO_BACK
            const isToPreApproved =
                currentWorkorder.toStatus === WorkorderStatus.PRE_APPROVED
            const isToApproved =
                currentWorkorder.toStatus === WorkorderStatus.APPROVED
            const isToPause =
                currentWorkorder.toStatus === WorkorderStatus.PAUSE
            const isToAssigned =
                currentWorkorder.toStatus === WorkorderStatus.ASSIGNED
            const isToInvoicedOut =
                currentWorkorder.toStatus === WorkorderStatus.INVOICED_OUT

            prev[WorkorderStatus.IN_PROGRESS].validCount += Number(
                isInProgressValid,
            )
            prev[WorkorderStatus.IN_PROGRESS].foundCount += Number(
                isToInProgress,
            )

            prev[WorkorderStatus.COMPLETE].validCount += Number(isCompleteValid)
            prev[WorkorderStatus.COMPLETE].foundCount += Number(isToComplete)

            prev[WorkorderStatus.GO_BACK].validCount += Number(isGoBackValid)
            prev[WorkorderStatus.GO_BACK].foundCount += Number(isToGoBack)

            prev[WorkorderStatus.PRE_APPROVED].validCount += Number(
                isPreApprovedValid,
            )
            prev[WorkorderStatus.PRE_APPROVED].foundCount += Number(
                isToPreApproved,
            )

            prev[WorkorderStatus.APPROVED].validCount += Number(isApprovedValid)
            prev[WorkorderStatus.APPROVED].foundCount += Number(isToApproved)

            prev[WorkorderStatus.PAUSE].validCount += Number(isPauseValid)
            prev[WorkorderStatus.PAUSE].foundCount += Number(isToPause)

            prev[WorkorderStatus.ASSIGNED].validCount += Number(isAssignedValid)
            prev[WorkorderStatus.ASSIGNED].foundCount += Number(isToAssigned)

            prev[WorkorderStatus.INVOICED_OUT].validCount += Number(
                isInvoicedOutValid,
            )
            prev[WorkorderStatus.INVOICED_OUT].foundCount += Number(
                isToInvoicedOut,
            )

            return { ...prev }
        },
        {
            [WorkorderStatus.IN_PROGRESS]: {
                validCount: 0,
                foundCount: 0,
            },
            [WorkorderStatus.COMPLETE]: {
                validCount: 0,
                foundCount: 0,
            },
            [WorkorderStatus.GO_BACK]: {
                validCount: 0,
                foundCount: 0,
            },
            [WorkorderStatus.PRE_APPROVED]: {
                validCount: 0,
                foundCount: 0,
            },

            [WorkorderStatus.APPROVED]: {
                validCount: 0,
                foundCount: 0,
            },
            [WorkorderStatus.PAUSE]: {
                validCount: 0,
                foundCount: 0,
            },
            [WorkorderStatus.ASSIGNED]: {
                validCount: 0,
                foundCount: 0,
            },
            [WorkorderStatus.INVOICED]: {
                validCount: 0,
                foundCount: 0,
            },
            [WorkorderStatus.INVOICE_REQUEST]: {
                validCount: 0,
                foundCount: 0,
            },
            [WorkorderStatus.INVOICED_OUT]: {
                validCount: 0,
                foundCount: 0,
            },
        },
    )

    const isChecked = (status: WorkorderStatus) => {
        let validCount = totalCounts[status].validCount
        if (isAccountManager(workspaceUser)) {
            validCount =
                status === WorkorderStatus.APPROVED
                    ? workorderTransitionList.filter(
                          (wo) => !isApprovalRoleRestricted(wo.workorder),
                      ).length
                    : workorderTransitionList.length
        }

        return validCount === totalCounts[status].foundCount && validCount != 0
    }

    const isIndeterminate = (status: WorkorderStatus) => {
        let validCount = totalCounts[status].validCount
        if (isAccountManager(workspaceUser)) {
            validCount =
                status === WorkorderStatus.APPROVED
                    ? workorderTransitionList.filter(
                          (wo) => !isApprovalRoleRestricted(wo.workorder),
                      ).length
                    : workorderTransitionList.length
        }

        return (
            (totalCounts[status].foundCount > 0 &&
                totalCounts[status].foundCount !== validCount) ||
            validCount === 0
        )
    }

    const isDisabled = (status: WorkorderStatus) => {
        if (isAccountManager(workspaceUser)) {
            return (
                status === WorkorderStatus.APPROVED &&
                workorderTransitionList.every((wo) =>
                    isApprovalRoleRestricted(wo.workorder),
                )
            )
        }

        return totalCounts[status].validCount === 0
    }

    const handleOnChange = (status: WorkorderStatus) => {
        const today = new Date()

        if (
            totalCounts[status].foundCount > 0 &&
            totalCounts[status].foundCount !== totalCounts[status].validCount
        ) {
            setWorkorderTransitionList(
                workorderTransitionList.map((wo) => {
                    const disabled =
                        isVendor &&
                        new Date(wo.workorder.start_date ?? '') > today
                    if (wo.toStatus === status && !disabled) {
                        return {
                            workorder: wo.workorder,
                            toStatus: undefined,
                        }
                    } else {
                        return {
                            workorder: wo.workorder,
                            toStatus: wo.toStatus,
                        }
                    }
                }),
            )
        } else if (totalCounts[status].foundCount === 0) {
            setWorkorderTransitionList(
                workorderTransitionList.map((wo) => {
                    const disabled =
                        isVendor &&
                        new Date(wo.workorder.start_date ?? '') > today
                    const approvalRoleDisabled =
                        status === WorkorderStatus.APPROVED &&
                        isApprovalRoleRestricted(wo.workorder)
                    if (
                        !approvalRoleDisabled &&
                        (isAccountManager(workspaceUser) ||
                            (transitions[wo.workorder.status].includes(
                                status,
                            ) &&
                                !disabled))
                    ) {
                        return {
                            workorder: wo.workorder,
                            toStatus: status,
                        }
                    } else {
                        return {
                            workorder: wo.workorder,
                            toStatus: wo.toStatus,
                        }
                    }
                }),
            )
        } else if (
            totalCounts[status].foundCount === totalCounts[status].validCount
        ) {
            setWorkorderTransitionList(
                workorderTransitionList.map((wo) => {
                    const disabled =
                        isVendor &&
                        new Date(wo.workorder.start_date ?? '') > today
                    if (wo.toStatus === status && !disabled) {
                        return {
                            workorder: wo.workorder,
                            toStatus: undefined,
                        }
                    } else {
                        return {
                            workorder: wo.workorder,
                            toStatus: wo.toStatus,
                        }
                    }
                }),
            )
        }
    }

    const theme = useTheme()

    const handleTransition = (transitionsArray: TransitionStatusDict[]) => {
        axiosInstance
            .post('workorder/super_transition/', {
                workorders: transitionsArray,
            })
            .then((res) => {
                dispatch(
                    updateBulkWorkorders(
                        res.data.map(createWorkorderFromResponse),
                    ),
                )
                toast.success('Bulk Transition Complete')
                setHandlingTransitions(false)
                handleOpen()
                setSelectedWorkorders({})
                if (handleDataFormClose) {
                    handleDataFormClose()
                }
                handleBulkEditClose()
            })
    }

    return (
        <Modal
            open={isOpen}
            onClose={handleOpen}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={isOpen}>
                <Paper
                    style={{
                        width: '90%',
                        display: 'flex',
                        overflow: 'hidden',
                        flexDirection: 'column',
                        height: '90%',
                    }}
                >
                    <Container>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h5),
                                fontWeight: 700,
                                marginTop: theme.spacing(1),
                                marginLeft: theme.spacing(2),
                            }}
                        >
                            Bulk Transition Editor
                        </span>
                        <div style={{ flex: 1 }} />

                        <Tooltip title="Cancel">
                            <IconButton onClick={handleOpen}>
                                <CloseIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </Container>
                    <Container
                        style={{
                            alignItems: 'center',
                            borderBottom: `1px solid ${theme.palette.grey[400]}`,
                        }}
                    >
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                flex: 1,
                                justifyContent: 'center',
                                padding: theme.spacing(2),
                            }}
                        >
                            Workorder
                        </Container>
                        {(isVendor ||
                            hasPermission(
                                workspaceUser,
                                SUPER_TRANSITION_REVERSAL,
                            )) && (
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 700,
                                    flex: 1,
                                    justifyContent: 'center',
                                    padding: theme.spacing(2),
                                    alignItems: 'center',
                                }}
                            >
                                Assigned
                                <Checkbox
                                    checked={isChecked(
                                        WorkorderStatus.ASSIGNED,
                                    )}
                                    indeterminate={isIndeterminate(
                                        WorkorderStatus.ASSIGNED,
                                    )}
                                    disabled={isDisabled(
                                        WorkorderStatus.ASSIGNED,
                                    )}
                                    onChange={() =>
                                        handleOnChange(WorkorderStatus.ASSIGNED)
                                    }
                                />
                                {totalCounts[WorkorderStatus.ASSIGNED]
                                    .foundCount > 0 && (
                                    <Container>
                                        (
                                        {
                                            totalCounts[
                                                WorkorderStatus.ASSIGNED
                                            ].foundCount
                                        }
                                        )
                                    </Container>
                                )}
                            </Container>
                        )}
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                flex: 1,
                                justifyContent: 'center',
                                padding: theme.spacing(2),
                                alignItems: 'center',
                            }}
                        >
                            In Progress
                            <Checkbox
                                checked={isChecked(WorkorderStatus.IN_PROGRESS)}
                                indeterminate={isIndeterminate(
                                    WorkorderStatus.IN_PROGRESS,
                                )}
                                disabled={isDisabled(
                                    WorkorderStatus.IN_PROGRESS,
                                )}
                                onChange={() =>
                                    handleOnChange(WorkorderStatus.IN_PROGRESS)
                                }
                            />
                            {totalCounts[WorkorderStatus.IN_PROGRESS]
                                .foundCount > 0 && (
                                <Container>
                                    (
                                    {
                                        totalCounts[WorkorderStatus.IN_PROGRESS]
                                            .foundCount
                                    }
                                    )
                                </Container>
                            )}
                        </Container>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                flex: 1,
                                justifyContent: 'center',
                                padding: theme.spacing(2),
                                alignItems: 'center',
                            }}
                        >
                            Complete
                            <Checkbox
                                checked={isChecked(WorkorderStatus.COMPLETE)}
                                indeterminate={isIndeterminate(
                                    WorkorderStatus.COMPLETE,
                                )}
                                disabled={isDisabled(WorkorderStatus.COMPLETE)}
                                onChange={() =>
                                    handleOnChange(WorkorderStatus.COMPLETE)
                                }
                            />
                            {totalCounts[WorkorderStatus.COMPLETE].foundCount >
                                0 && (
                                <Container>
                                    (
                                    {
                                        totalCounts[WorkorderStatus.COMPLETE]
                                            .foundCount
                                    }
                                    )
                                </Container>
                            )}
                        </Container>
                        {!isVendor && (
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 700,
                                    flex: 1,
                                    justifyContent: 'center',
                                    padding: theme.spacing(2),
                                    alignItems: 'center',
                                }}
                            >
                                Go Back
                                <Checkbox
                                    checked={isChecked(WorkorderStatus.GO_BACK)}
                                    indeterminate={isIndeterminate(
                                        WorkorderStatus.GO_BACK,
                                    )}
                                    disabled={isDisabled(
                                        WorkorderStatus.GO_BACK,
                                    )}
                                    onChange={() =>
                                        handleOnChange(WorkorderStatus.GO_BACK)
                                    }
                                />
                                {totalCounts[WorkorderStatus.GO_BACK]
                                    .foundCount > 0 && (
                                    <Container>
                                        (
                                        {
                                            totalCounts[WorkorderStatus.GO_BACK]
                                                .foundCount
                                        }
                                        )
                                    </Container>
                                )}
                            </Container>
                        )}
                        {isVendor && (
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 700,
                                    flex: 1,
                                    justifyContent: 'center',
                                    padding: theme.spacing(2),
                                    alignItems: 'center',
                                }}
                            >
                                Pause
                                <Checkbox
                                    checked={isChecked(WorkorderStatus.PAUSE)}
                                    indeterminate={isIndeterminate(
                                        WorkorderStatus.PAUSE,
                                    )}
                                    disabled={isDisabled(WorkorderStatus.PAUSE)}
                                    onChange={() =>
                                        handleOnChange(WorkorderStatus.PAUSE)
                                    }
                                />
                                {totalCounts[WorkorderStatus.PAUSE].foundCount >
                                    0 && (
                                    <Container>
                                        (
                                        {
                                            totalCounts[WorkorderStatus.PAUSE]
                                                .foundCount
                                        }
                                        )
                                    </Container>
                                )}
                            </Container>
                        )}
                        {!isVendor && (
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 700,
                                    flex: 1,
                                    justifyContent: 'center',
                                    padding: theme.spacing(2),
                                    alignItems: 'center',
                                }}
                            >
                                Pre Approved
                                <Checkbox
                                    checked={isChecked(
                                        WorkorderStatus.PRE_APPROVED,
                                    )}
                                    indeterminate={isIndeterminate(
                                        WorkorderStatus.PRE_APPROVED,
                                    )}
                                    disabled={isDisabled(
                                        WorkorderStatus.PRE_APPROVED,
                                    )}
                                    onChange={() =>
                                        handleOnChange(
                                            WorkorderStatus.PRE_APPROVED,
                                        )
                                    }
                                />
                                {totalCounts[WorkorderStatus.PRE_APPROVED]
                                    .foundCount > 0 && (
                                    <Container>
                                        (
                                        {
                                            totalCounts[
                                                WorkorderStatus.PRE_APPROVED
                                            ].foundCount
                                        }
                                        )
                                    </Container>
                                )}
                            </Container>
                        )}
                        {!isVendor && (
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 700,
                                    flex: 1,
                                    justifyContent: 'center',
                                    padding: theme.spacing(2),
                                    alignItems: 'center',
                                }}
                            >
                                Approved
                                <Checkbox
                                    checked={isChecked(
                                        WorkorderStatus.APPROVED,
                                    )}
                                    indeterminate={isIndeterminate(
                                        WorkorderStatus.APPROVED,
                                    )}
                                    disabled={isDisabled(
                                        WorkorderStatus.APPROVED,
                                    )}
                                    onChange={() =>
                                        handleOnChange(WorkorderStatus.APPROVED)
                                    }
                                />
                                {totalCounts[WorkorderStatus.APPROVED]
                                    .foundCount > 0 && (
                                    <Container>
                                        (
                                        {
                                            totalCounts[
                                                WorkorderStatus.APPROVED
                                            ].foundCount
                                        }
                                        )
                                    </Container>
                                )}
                            </Container>
                        )}
                        {!isVendor && (
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 700,
                                    flex: 1,
                                    justifyContent: 'center',
                                    padding: theme.spacing(2),
                                    alignItems: 'center',
                                }}
                            >
                                Invoiced Out
                                <Checkbox
                                    checked={isChecked(
                                        WorkorderStatus.INVOICED_OUT,
                                    )}
                                    indeterminate={isIndeterminate(
                                        WorkorderStatus.INVOICED_OUT,
                                    )}
                                    disabled={isDisabled(
                                        WorkorderStatus.INVOICED_OUT,
                                    )}
                                    onChange={() =>
                                        handleOnChange(
                                            WorkorderStatus.INVOICED_OUT,
                                        )
                                    }
                                />
                                {totalCounts[WorkorderStatus.INVOICED_OUT]
                                    .foundCount > 0 && (
                                    <Container>
                                        (
                                        {
                                            totalCounts[
                                                WorkorderStatus.INVOICED_OUT
                                            ].foundCount
                                        }
                                        )
                                    </Container>
                                )}
                            </Container>
                        )}
                    </Container>
                    <Container style={{ flexDirection: 'column' }} scrollY>
                        {workorderTransitionList.map((woDict) => {
                            const rowTransitions = isApprovalRoleRestricted(
                                woDict.workorder,
                            )
                                ? {
                                      ...transitions,
                                      [woDict.workorder.status]: transitions[
                                          woDict.workorder.status
                                      ].filter(
                                          (status) =>
                                              status !==
                                              WorkorderStatus.APPROVED,
                                      ),
                                  }
                                : transitions

                            return (
                                <WorkorderRow
                                    key={woDict.workorder.id}
                                    workorder={woDict.workorder}
                                    transitions={rowTransitions}
                                    toStatus={woDict.toStatus}
                                    onStatusChange={(
                                        status: string | undefined,
                                    ) => {
                                        const newWorkorderList = workorderTransitionList.map(
                                            (innerWoDict) => {
                                                if (
                                                    innerWoDict.workorder.id ===
                                                    woDict.workorder.id
                                                ) {
                                                    return {
                                                        workorder:
                                                            woDict.workorder,
                                                        toStatus: status,
                                                    }
                                                }
                                                return innerWoDict
                                            },
                                        )
                                        setWorkorderTransitionList(
                                            newWorkorderList,
                                        )
                                    }}
                                    upStreamContractMap={upStreamContractMap}
                                    isVendor={isVendor}
                                />
                            )
                        })}
                    </Container>
                    <Container
                        style={{
                            flex: 1,
                            borderBottom: `1px solid ${theme.palette.grey[400]}`,
                        }}
                    ></Container>

                    <Container
                        style={{
                            padding: theme.spacing(2),
                            justifyContent: 'flex-end',
                        }}
                    >
                        {handlingTransitions && (
                            <CircularProgress
                                size={20}
                                style={{ margin: theme.spacing(1) }}
                            />
                        )}
                        {!handlingTransitions && (
                            <Button
                                color="primary"
                                variant="outlined"
                                style={{ marginRight: theme.spacing(2) }}
                                onClick={() => {
                                    setHandlingTransitions(true)
                                    const transitionsArray: TransitionStatusDict[] = []
                                    workorderTransitionList.map((wo) => {
                                        if (wo.toStatus !== undefined) {
                                            transitionsArray.push({
                                                id: wo.workorder.id,
                                                status: wo.toStatus,
                                                revert: false,
                                            })
                                        }
                                    })
                                    if (transitionsArray.length !== 0) {
                                        if (superTransitionWorkorders) {
                                            superTransitionWorkorders(
                                                transitionsArray,
                                                true,
                                            )
                                                .then(() => {
                                                    toast.success(
                                                        `Bulk transition complete`,
                                                    )
                                                    handleOpen()
                                                    setSelectedWorkorders({})
                                                    if (handleDataFormClose) {
                                                        handleDataFormClose()
                                                    }
                                                    handleBulkEditClose()
                                                })
                                                .catch((e) => {
                                                    toast.error(
                                                        e.response.data.message,
                                                    )
                                                })
                                                .finally(() => {
                                                    setHandlingTransitions(
                                                        false,
                                                    )
                                                })
                                        } else {
                                            handleTransition(transitionsArray)
                                        }
                                    }
                                }}
                            >
                                Transition
                            </Button>
                        )}
                        <Button
                            color="secondary"
                            variant="outlined"
                            style={{ marginRight: theme.spacing(2) }}
                            onClick={handleOpen}
                        >
                            Cancel
                        </Button>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
